import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetItem, GetItemTickets } from '../../services/activities';

// 상품 정보를 가져오는 Thunk
export const fetchItem = createAsyncThunk(
  'tickets/fetchItem',
  async (id, { rejectWithValue }) => {
    try {
      const data = await GetItem(id);

      let parsedImages = [];
      if (typeof data.images === "string" && data.images.length > 1) {
        try {
          parsedImages = JSON.parse(data.images);
          if (!Array.isArray(parsedImages)) parsedImages = [];
        } catch (error) {
          console.error("Error parsing images:", error);
          parsedImages = [];
        }
      }

      return {
        ...data,
        images: parsedImages.length > 0 ? Array.from(new Set(parsedImages)) : ["/assets/tempAct.jpg"],
        categories: typeof data.categories === "string"
          ? JSON.parse(data.categories)
          : data.categories || [],
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 티켓 정보를 가져오는 Thunk
export const fetchTickets = createAsyncThunk("tickets/fetchTickets", async ({ id, selectedDate }) => {
  const params = { selectedDay: selectedDate };
  let response = await GetItemTickets(id, params);
  
  const mergedTickets = {};
  let rateData = {};
  let personTypesSet = new Set();

  if (!Array.isArray(response)) {
    response = [response];
  }

  response.forEach( ticket => {
    if (ticket.tickets && ticket.tickets.dates) {
      const providerUrlMap = Object.fromEntries(ticket.url.map(({ platform, url }) => [platform, url]));

      ticket.tickets.dates.forEach(dateItem => {
        dateItem.ticketTypes.forEach(ticketType => {
          const ticketTypeName = `${dateItem.date} - ${ticketType.name}`;

          if (!mergedTickets[ticketTypeName]) {
            mergedTickets[ticketTypeName] = {};
          }

          ticketType.prices.forEach(priceInfo => {
            const platformName = priceInfo.provider;
            const categories = priceInfo.categories;

            Object.entries(categories).forEach(([personType, price]) => {
              personTypesSet.add(personType);

              if (!mergedTickets[ticketTypeName][personType]) {
                mergedTickets[ticketTypeName][personType] = {};
              }

              mergedTickets[ticketTypeName][personType][platformName] = {
                price: price,
                url: providerUrlMap[platformName] || ""
              };
            });
          });
        });
      });
    }

    if (Object.keys(rateData).length === 0) {
      rateData = {
        rating: ticket.rating || "N/A",
        rating_count: ticket.rating_count || "0"
      };
    }
  });

  return {
    tickets: mergedTickets,
    rate: rateData,
    personOptions: [
      { label: "All", value: "all" },
      ...[...personTypesSet].map(name => ({ label: name, value: name }))
    ]
  };
});

const ticketSlice = createSlice({
  name: "tickets",
  initialState: {
    item: null,
    tickets: {},
    rate: {},
    personOptions: [{ label: "All", value: "all" }],
    selectedDate: new Date().toISOString().split("T")[0],
    selectedPersonType: "all",
    loading: false,
    error: null,
  },
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedPersonType: (state, action) => {
      state.selectedPersonType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchItem.fulfilled, (state, action) => {
        state.loading = false;
        state.item = action.payload;
      })
      .addCase(fetchItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.tickets = action.payload.tickets;
        state.rate = action.payload.rate;
        state.personOptions = action.payload.personOptions;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedDate, setSelectedPersonType } = ticketSlice.actions;
export const selectItem = (state) => state.tickets.item;
export const selectFilteredTickets = (state) => state.tickets.tickets;
export const selectPersonOptions = (state) => state.tickets.personOptions;
export const selectSelectedDate = (state) => state.tickets.selectedDate;
export const selectSelectedPersonType = (state) => state.tickets.selectedPersonType;
export const selectRate = (state) => state.tickets.rate;

export default ticketSlice.reducer;
